import {
  AcompanhamentoAluno,
  AcompanhamentoAlunoDisciplina,
  AcompanhamentoComponenteCurricular,
  AcompanhamentoFormulario,
  AcompanhamentoPedagogicoRegistro,
  AcompanhamentoPeriodo
} from '../models/acompanhamento-pedagogico.types';
import {
  AcompanhamentoApiCampoAdicional,
  AcompanhamentoApiEnturmacao,
  AcompanhamentoApiItemEducacional,
  AcompanhamentoApiPeriodo,
  AcompanhamentoApiRegistro,
  AcompanhamentoApiSituacaoItemEducacional
} from '../models/acompanhamento-pedagogico.api.types';
import { normalizarCamposDecimaisConfiguracao } from '../../../utils/componentes/cpa-grid-bridge/cpa-grid-bridge-decimal-normalizer';

export class AcompanhamentoPedagogicoMapper {
  static toPeriodo(periodo: AcompanhamentoApiPeriodo, hojeIso: string): AcompanhamentoPeriodo {
    return {
      id: periodo.id,
      descricao: periodo.descricao,
      dataInicial: periodo.dataInicial,
      dataFinal: periodo.dataFinal,
      encerrado: !!periodo.encerrado,
      ativo: hojeIso >= periodo.dataInicial && hojeIso <= periodo.dataFinal
    };
  }

  static toComponente(item: AcompanhamentoApiItemEducacional): AcompanhamentoComponenteCurricular {
    return {
      id: item.pai?.id || item.id,
      descricao: this.getItemEducacionalDescricao(item),
      ativo: true,
      itemEducacionalId: item.id,
      etapaMatrizDisciplinaId: item.etapaMatrizDisciplina?.id || null
    };
  }

  static toAluno(
    enturmacao: AcompanhamentoApiEnturmacao,
    itensTurma: AcompanhamentoApiItemEducacional[]
  ): AcompanhamentoAluno {
    const dataInicioEnturmacao = this.getDataInicioEnturmacao(enturmacao);

    return {
      id: enturmacao.id,
      nome: enturmacao.matricula?.nome || enturmacao.matricula?.nomeAluno || 'Aluno sem nome',
      nomeSocial: enturmacao.matricula?.nomeFantasia || null,
      // A API de elegibilidade ja retorna somente enturmacoes validas para a data informada.
      ativo: true,
      dataMatricula: dataInicioEnturmacao,
      possuiDeficienciaOuTranstorno:
        !!enturmacao.matricula?.possuiDeficiencia || !!enturmacao.matricula?.possuiTranstornosGlobais,
      possuiAltasHabilidades: !!enturmacao.matricula?.possuiAltasHabilidades,
      necessitaAEE: !!enturmacao.matricula?.necessitaAee,
      disciplinas: this.toAlunoDisciplinas(enturmacao, itensTurma),
      // Armazenar enturmação original para uso no payload POST
      _enturmacaoOriginal: enturmacao
    };
  }

  static toRegistro(registro: AcompanhamentoApiRegistro): AcompanhamentoPedagogicoRegistro {
    const enturmacao = this.getEnturmacaoPrincipal(registro);
    const itemEducacional = this.getItemEducacional(registro.itemEducacional);

    return {
      id: registro.id || 0,
      periodoId: 0,
      componenteId: itemEducacional?.id || 0,
      disciplina: this.getItemEducacionalDescricao(itemEducacional),
      alunoId: enturmacao?.id || registro.matricula?.id || 0,
      alunoNome:
        enturmacao?.nomeAluno ||
        enturmacao?.matricula?.nome ||
        enturmacao?.matricula?.nomeAluno ||
        registro.matricula?.nome ||
        registro.matricula?.nomeAluno ||
        'Aluno sem nome',
      alunoNomeSocial:
        enturmacao?.matricula?.nomeFantasia ||
        registro.matricula?.nomeFantasia ||
        null,
      data: registro.dataRegistro || registro.data || '',
      observacao: registro.observacao || '',
      publicado: !!registro.publicar,
      dataPublicacao: registro.dataPublicacao || null,
      campoAdicional: (registro.campoAdicional as Record<string, unknown> | null) || null,
      arquivos: (registro.arquivos || []).map((a) => ({
        id: a.id,
        nome: a.nome,
        tamanho: a.tamanho
      }))
    };
  }

  static toSavePayload(params: {
    formulario: AcompanhamentoFormulario;
    turmaId: number;
    estabelecimentoId: number;
    componentes: AcompanhamentoComponenteCurricular[];
    alunos: AcompanhamentoAluno[];
    campoAdicional?: Record<string, unknown> | null;
    registroAtual?: AcompanhamentoApiRegistro;
  }): AcompanhamentoApiRegistro {
    const { formulario, turmaId, estabelecimentoId, componentes, alunos, campoAdicional, registroAtual } = params;
    const componente = componentes.find((item) => item.id === formulario.componenteId);
    // Usar enturmações originais (completas) do objeto aluno
    const enturmacoes = formulario.alunoIds
      .map((alunoId) => {
        const aluno = alunos.find((a) => a.id === alunoId);
        if (aluno && (aluno as any)._enturmacaoOriginal) {
          return (aluno as any)._enturmacaoOriginal;
        }
        // Fallback: criar estrutura mínima
        return {
          id: aluno?.id,
          matricula: {
            id: aluno?.id,
            nome: aluno?.nome
          }
        };
      })
      .filter((enturmacao) => !!enturmacao);

    const campoAdicionalPayload = this.normalizeCampoAdicionalParaPayload(
      campoAdicional ?? registroAtual?.campoAdicional ?? null,
      !formulario.id
    );

    return {
      ...registroAtual,
      id: formulario.id || registroAtual?.id,
      turma: { id: turmaId },
      estabelecimento: { id: estabelecimentoId },
      itemEducacional: componente ? { id: componente.id, descricao: componente.descricao } : null,
      dataRegistro: formulario.data,
      observacao: formulario.observacao.trim(),
      enturmacoes,
      campoAdicional: campoAdicionalPayload,
      publicar: !!formulario.publicar,
      dataPublicacao: registroAtual?.dataPublicacao || null,
      arquivos: (formulario.arquivos || []).map((a) => ({
        id: a.id,
        nome: a.nome,
        tamanho: a.tamanho
      }))
    };
  }

  private static normalizeCampoAdicionalParaPayload(
    value: unknown,
    isNovoRegistro: boolean
  ): AcompanhamentoApiCampoAdicional[] | null {
    if (!value) {
      return null;
    }

    const lista = Array.isArray(value)
      ? value
      : (typeof value === 'object' ? [value] : []);

    if (!lista.length) {
      return null;
    }

    const sanitize = (input: any): any => {
      if (Array.isArray(input)) {
        return input.map((item) => sanitize(item));
      }
      if (input && typeof input === 'object') {
        const clone: any = {};
        Object.keys(input).forEach((key) => {
          if (key === '$$hashKey') {
            return;
          }
          clone[key] = sanitize(input[key]);
        });
        return clone;
      }
      return input;
    };

    let payload = sanitize(lista) as AcompanhamentoApiCampoAdicional[];

    payload = payload.map((item) =>
      normalizarCamposDecimaisConfiguracao(item as Record<string, unknown>)
    ) as AcompanhamentoApiCampoAdicional[];

    if (isNovoRegistro) {
      payload = payload.map((item) => ({
        ...item,
        id: null,
        origem: null
      }));
    }

    return payload;
  }

  static getItemEducacionalDescricao(item?: AcompanhamentoApiItemEducacional | number | null): string {
    if (!item || typeof item === 'number') {
      return '';
    }

    return (
      item.descricao ||
      item.nome ||
      item.pai?.descricao ||
      item.pai?.nome ||
      item.etapaMatrizDisciplina?.disciplina?.descricao ||
      item.etapaMatrizDisciplina?.campoExperiencia?.descricao ||
      item.etapaMatrizDisciplina?.eixoTematico?.descricao ||
      item.atividade?.descricao ||
      'Componente curricular'
    );
  }

  private static toAlunoDisciplinas(
    enturmacao: AcompanhamentoApiEnturmacao,
    itensTurma: AcompanhamentoApiItemEducacional[]
  ): AcompanhamentoAlunoDisciplina[] {
    const situacoes = enturmacao.situacaoItensEducacionais || [];
    const dataInicioEnturmacao = this.getDataInicioEnturmacao(enturmacao);

    if (situacoes.length) {
      return situacoes
        .filter((situacao) => situacao.itemEducacional?.id)
        .map((situacao) => {
          const itemId = situacao.itemEducacional?.id || 0;
          // pai pode vir embutido na resposta da API ou pode ser resolvido
          // buscando no itensTurma (filhos) pelo id do item filho.
          const componenteId =
            situacao.itemEducacional?.pai?.id ||
            itensTurma.find((it) => it.id === itemId)?.pai?.id ||
            itemId;
          return {
            componenteId,
            dataMatricula: dataInicioEnturmacao,
            dataFim: enturmacao.dataFim || null,
            dispensado: situacao.situacao === 'DISPENSADO',
            ativo: this.isSituacaoAtiva(situacao)
          };
        });
    }

    // Sem situacaoItensEducacionais: aluno matriculado na turma sem vínculo por
    // componente. [] faz o filtro por componente aceitá-lo incondicionalmente.
    return [];
  }

  private static getDataInicioEnturmacao(enturmacao: AcompanhamentoApiEnturmacao): string {
    return enturmacao.dataInicio || enturmacao.dataInicial || '';
  }

  private static isSituacaoAtiva(situacao: AcompanhamentoApiSituacaoItemEducacional): boolean {
    return situacao.situacao !== 'CANCELADO' && situacao.situacao !== 'TRANSFERIDO';
  }

  private static getEnturmacaoPrincipal(registro: AcompanhamentoApiRegistro): AcompanhamentoApiEnturmacao | null {
    if (registro.enturmacao) {
      return registro.enturmacao;
    }

    if (Array.isArray(registro.enturmacoes)) {
      const enturmacao = registro.enturmacoes.find(
        (item): item is AcompanhamentoApiEnturmacao => !!item && typeof item !== 'number'
      );
      return enturmacao || null;
    }

    return null;
  }

  private static getItemEducacional(
    item?: AcompanhamentoApiItemEducacional | number | null
  ): AcompanhamentoApiItemEducacional | null {
    if (!item || typeof item === 'number') {
      return null;
    }

    return item;
  }

}
